import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import { GridReadyEvent, PaginationChangedEvent } from "ag-grid-community";

import { useReportContext } from "../report-context";

import { FormattedTableContentProps } from "./types";
import { useFormattedTableData } from "./hooks/useFormattedTableData";
import { useTableColumns } from "./hooks/useTableColumns";
import { useColumnEvents } from "./hooks/useColumnEvents";
import EvalTableShimmer from "./EvalTableShimmer";
import { CellStatus } from "./RowCell/Cells/primitives";

import { TableV2, ViewMode } from "@/components/ui/TableV2";
import { ColumnType, ReportCell, ReportColumn } from "@/types/evaluate";

export const FormattedTableContent = observer(
  ({
    data,
    emptyMessage,
    currentPage,
    pageSize,
    onColumnMoved,
    gridContextStorageKey,
    gridContextCustomActions,
    sideBar,
    rowNumbers,
  }: FormattedTableContentProps) => {
    const gridRef = useRef<AgGridReact>(null);
    const hasRows = !!data?.length;
    const reportContext = useReportContext();
    const { reportId, isEditable, selectedColumn, sortedColumns } = reportContext;

    const { tableData, isLoading } = useFormattedTableData(data);
    const { tableColumnsWithAddNewColumn } = useTableColumns(gridRef);
    const { handleNewColumnsLoaded } = useColumnEvents(reportId);

    const handleToolPanelVisibleChanged = useCallback(() => {
      if (gridRef.current?.api) {
        const lastvisivble = gridRef.current.api.getColumns()?.find(column => column.isVisible());

        if (lastvisivble) {
          gridRef.current.api.applyColumnState({
            state: [{ colId: lastvisivble.getColId(), flex: 1 }],
          });
        }
      }
    }, []);

    const handlePaginationChanged = useCallback((event: PaginationChangedEvent) => {
      const { api } = event;
      const newCurrentPage = api.paginationGetCurrentPage() + 1; // ag-grid uses 0-based indexing
      const newPageSize = api.paginationGetPageSize();

      reportContext.setCurrentPage(newCurrentPage);
      reportContext.setPageSize(newPageSize);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (selectedColumn) {
        const gridColumn = gridRef.current?.api?.getColumn(`col_${selectedColumn.id}`);
        if (gridColumn) {
          setTimeout(() => {
            gridRef.current?.api?.ensureColumnVisible(gridColumn);
          }, 300);
        }
      }
    }, [selectedColumn]);

    const handleGridReady = useCallback((event: GridReadyEvent) => {
      const isAddNewColumnHidden = !event.api?.getColumn(`col_${-99999999}`)?.isVisible();

      if (isAddNewColumnHidden) {
        event.api?.applyColumnState({
          state: [{ colId: `col_${-99999999}`, hide: false }],
        });
      }
    }, []);

    useEffect(() => {
      if (gridRef.current?.api) {
        const lastColumn = gridRef.current.api.getColumn(
          `col_${tableColumnsWithAddNewColumn[tableColumnsWithAddNewColumn.length - 2].id}`,
        );

        if (lastColumn) {
          gridRef.current.api.applyColumnState({
            state: [{ colId: lastColumn.getColId(), hide: false, flex: 1 }],
          });
        }
      }
    }, [tableColumnsWithAddNewColumn]);

    useEffect(() => {
      if (gridRef.current?.api && sortedColumns.length > 0) {
        const gridApi = gridRef.current.api;

        const columnState = sortedColumns.map((column: ReportColumn) => {
          const colId = `col_${column.id}`;
          const existingColumn = gridApi.getColumn(colId);
          const currentlyHidden = existingColumn ? !existingColumn.isVisible() : false;

          return {
            colId,
            sort: null,
            sortIndex: null,
            hide: currentlyHidden,
          };
        });

        gridApi.applyColumnState({
          state: columnState,
          applyOrder: true,
        });
      }
    }, [sortedColumns]);

    const suppressMovableColumns = useMemo(
      () =>
        reportContext.reportCells?.some((cell: ReportCell) => {
          const column = reportContext.reportColumns?.find(
            (col: ReportColumn) => col.id === cell.report_column_id,
          );
          return (
            (column?.column_type !== ColumnType.HUMAN && cell.status === CellStatus.QUEUED) ||
            cell.status === CellStatus.RUNNING
          );
        }),
      [reportContext.reportCells, reportContext.reportColumns],
    );

    if (isLoading && !hasRows) {
      return <EvalTableShimmer />;
    }

    return (
      <div className="flex h-full flex-1 flex-col rounded-md">
        <TableV2
          gridRef={gridRef}
          data={tableData}
          columns={tableColumnsWithAddNewColumn}
          isLoading={isLoading}
          enableSorting
          enableResizing
          enablePagination
          currentPage={currentPage}
          pageSize={pageSize}
          autoFitRowsToHeight={isEditable}
          defaultViewMode={isEditable ? null : ViewMode.EXPANDED}
          suppressColumnVirtualisation
          shouldAutoSizeAllColumns
          sideBar={sideBar}
          cellSelection
          headerHeight={40}
          rowDragMultiRow
          rowNumbers={rowNumbers}
          hoverable
          onNewColumnsLoaded={handleNewColumnsLoaded}
          emptyStateMessage={emptyMessage}
          onGridReady={handleGridReady}
          suppressMovableColumns={suppressMovableColumns}
          onColumnMoved={onColumnMoved}
          gridContextStorageKey={gridContextStorageKey}
          gridContextCustomActions={gridContextCustomActions}
          onToolPanelVisibleChanged={handleToolPanelVisibleChanged}
          onPaginationChanged={handlePaginationChanged}
        />
      </div>
    );
  },
);
